import React from 'react';
import { Box, Button, IconButton } from '@mui/material';
import { VolumeUp as SpeakerIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import UnitPageShell from '../../components/shared/UnitPageShell';
import EditableTextBlock from '../../components/shared/EditableTextBlock';
import ZoomableImage from '../../components/shared/ZoomableImage';
import { useAuth } from '../../context/AuthContext';
import { useTTS } from '../../hooks/useTTS';
import { uploadUnitAsset, updateUnitContent, useUnitOverride } from '../../services/unitOverridesService';

const PAGE_ID = 'm1_u3_x1_info';

const M1_U3_X1_Info: React.FC = () => {
  const navigate = useNavigate();
  const { token, canEditTextsM1, canUploadImagesM1 } = useAuth();
  const canEditTexts = canEditTextsM1;
  const canUploadImages = canUploadImagesM1;

  // Override laden
  const { override, refresh } = useUnitOverride(PAGE_ID);

  // Basistexte
  const base = {
    hauptaufgabe:
      'Brandschutzklappen sind ein entscheidender Bestandteil von Lüftungsanlagen. Ihre Hauptaufgabe ist es, im Brandfall die Ausbreitung von Feuer und Rauch über die Lüftungskanäle in andere Brandabschnitte eines Gebäudes zu verhindern. Sie schließen automatisch, um den Luftstrom zu unterbrechen.',
    wichtigkeit:
      'Ohne Brandschutzklappen könnten Lüftungskanäle zu "Brücken" werden, die Feuer und Rauch schnell in andere Bereiche des Gebäudes tragen. Dies würde die Flucht- und Rettungswege gefährden und die Arbeit der Feuerwehr erschweren. BSK helfen auch, Sachschäden durch Rauch zu minimieren.',
    vorschriften:
      'Der Einbau von Brandschutzklappen ist gesetzlich vorgeschrieben und wird in den Landesbauordnungen und Sonderbauvorschriften geregelt. Sie müssen regelmäßig gewartet werden, um ihre Funktion im Notfall zu gewährleisten. Die Auslösung kann thermisch, durch Rauchmelder oder manuell erfolgen.'
  };

  const blocks = React.useMemo(() => ([
    { id: 'block_hauptaufgabe', heading: '1. Hauptaufgabe von Brandschutzklappen (BSK)', body: override?.texts?.['block_hauptaufgabe'] ?? base.hauptaufgabe },
    { id: 'block_wichtigkeit', heading: '2. Warum sind BSK so wichtig?', body: override?.texts?.['block_wichtigkeit'] ?? base.wichtigkeit },
    { id: 'block_vorschriften', heading: '3. Vorschriften und Wartung', body: override?.texts?.['block_vorschriften'] ?? base.vorschriften }
  ]), [override, base.hauptaufgabe, base.wichtigkeit, base.vorschriften]);

  // TTS aus Blöcken zusammensetzen
  const fullPlainText = React.useMemo(() => blocks.map(b => `${b.heading}. ${b.body}`).join(' '), [blocks]);
  const { speak, stop, speaking } = useTTS({ text: fullPlainText });

  // Admin: Bild ersetzen
  const handleImageReplace = React.useCallback(async (key: string, file: File) => {
    if (!token) return alert('Kein Token.');
    if (!canUploadImages) return alert('Keine Berechtigung fuer Bilder-Uploads.');
    try { await uploadUnitAsset(PAGE_ID, key, file, token); alert('Bild aktualisiert.'); refresh(); }
    catch (e: any) { alert('Upload fehlgeschlagen: ' + (e?.message || e)); }
  }, [token, refresh, canUploadImages]);

  // Admin: Text speichern
  const handleTextSave = React.useCallback(async (blockId: string, newBody: string) => {
    if (!token) return alert('Kein Token.');
    if (!canEditTexts) return alert('Keine Berechtigung zum Texte bearbeiten.');
    try { await updateUnitContent(PAGE_ID, { texts: { [blockId]: newBody } }, token); alert('Text gespeichert.'); refresh(); }
    catch (e: any) { alert('Speichern fehlgeschlagen: ' + (e?.message || e)); }
  }, [token, refresh, canEditTexts]);

  const ttsBtn = React.createElement(IconButton, { onClick: speaking ? stop : speak, 'aria-label': 'TTS', sx: { ml: 1 } }, React.createElement(SpeakerIcon, { sx: { fontSize: 28 } }));

  // Bildquellen mit Override-Fallback (unified pattern)
  const imgSrcFunc = (slot: string, fallback: string) => {
    const v = override?.images?.[slot];
    if (!v) return fallback;
    const s = String(v);
    return s.startsWith('/assets/') || s.startsWith('assets/') ? s : `/assets/unit_custom/${s}`;
  };

  const imgSrc = {
    img_0: imgSrcFunc('img_0', '/assets/drawable/bsk1.jpeg'),
    img_1: imgSrcFunc('img_1', '/assets/drawable/funktionsschema_brandschutzklappen.jpg'),
    img_2: imgSrcFunc('img_2', '/assets/drawable/paragraph.jpeg'),
    img_3: imgSrcFunc('img_3', '/assets/drawable/bsk_wartung.jpeg')
  } as const;

  return (
    <UnitPageShell title="1.3.1 Brandschutzklappen" actions={ttsBtn}>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
        {/* Linke Spalte */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <EditableTextBlock
            pageId={PAGE_ID}
            blockId="block_hauptaufgabe"
            heading="1. Hauptaufgabe von Brandschutzklappen (BSK)"
            body={blocks[0].body}
            onSave={(b) => handleTextSave('block_hauptaufgabe', b)}
          />

          <ZoomableImage
            src={imgSrc.img_0}
            alt="Brandschutzklappe im Kanal"
            label="Bild einfügen"
            onReplace={canUploadImages ? (f) => handleImageReplace('img_0', f) : undefined}
          />
        </Box>

        {/* Rechte Spalte */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <EditableTextBlock
            pageId={PAGE_ID}
            blockId="block_wichtigkeit"
            heading="2. Warum sind BSK so wichtig?"
            body={blocks[1].body}
            onSave={(b) => handleTextSave('block_wichtigkeit', b)}
          />

          <ZoomableImage
            src={imgSrc.img_1}
            alt="Funktionsschema BSK"
            label="Bild einfügen"
            onReplace={canUploadImages ? (f) => handleImageReplace('img_1', f) : undefined}
          />

          <EditableTextBlock
            pageId={PAGE_ID}
            blockId="block_vorschriften"
            heading="3. Vorschriften und Wartung"
            body={blocks[2].body}
            onSave={(b) => handleTextSave('block_vorschriften', b)}
          />

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <ZoomableImage
              src={imgSrc.img_2}
              alt="Paragraphenzeichen"
              label="Bild einfügen"
              onReplace={canUploadImages ? (f) => handleImageReplace('img_2', f) : undefined}
            />
            <ZoomableImage
              src={imgSrc.img_3}
              alt="Wartungssymbol"
              label="Bild einfügen"
              onReplace={canUploadImages ? (f) => handleImageReplace('img_3', f) : undefined}
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
            <Button variant="outlined" onClick={() => navigate('/units/m1_u3_x99_quiz')}>Zum Quiz</Button>
          </Box>
        </Box>
      </Box>
    </UnitPageShell>
  );
};

export default M1_U3_X1_Info;

